<template>
  <div class="flex h-screen gap-1">
    <component :is="sidebarComponent" />
    <div class="flex flex-col flex-1 px-2">
      <component :is="navbarComponent" />
      <main class="relative flex-1 overflow-y-scroll px-4 pe-8">
        <LoadingOverlay :show="dashboardStore.loading" />
        <div class="space-y-6">

          <div class="flex items-center justify-between">
            <DashboardHeader title="Dashboard" subtitle="Overview of your latest activity" />

            <label class="flex items-center gap-2 cursor-pointer select-none">
              <span class="text-xs text-base-content/50">Mock</span>
              <input
                type="checkbox"
                class="toggle toggle-xs"
                :checked="!dashboardStore.useMock"
                @change="toggleMock"
              />
              <span class="text-xs text-base-content/50">API</span>
            </label>
          </div>

          <Transition name="stat-fade" mode="out-in">
            <div
              v-if="dashboardStore.loading"
              key="loading"
              class="flex flex-wrap gap-6 justify-around w-full"
            >
              <div
                v-for="n in 4"
                :key="n"
                class="skeleton min-w-[160px] max-w-[220px] flex-1 aspect-square rounded-xl"
              />
            </div>
            <div
              v-else
              key="loaded"
              class="flex flex-wrap gap-6 justify-around w-full"
            >
              <StatCard
                v-for="stat in dashboardStore.stats"
                :key="stat.id"
                :title="stat.name"
                :count="stat.count"
                :total="stat.total"
                :status="stat.status"
                :color="stat.color"
                class="min-w-[160px] max-w-[220px] flex-1"
              />
            </div>
          </Transition>

          <Transition name="stat-fade" mode="out-in">
            <div v-if="dashboardStore.loading" key="loading" class="grid grid-cols-2 gap-6 w-full">
              <div class="skeleton h-80 rounded-xl" />
              <div class="skeleton h-80 rounded-xl" />
            </div>
            <div v-else key="loaded" class="grid grid-cols-2 gap-6 w-full">
              <LinesChart
                chartTitle="Acceptance rate"
                :chartDescription="linesChartDescription"
                :quotesLabel="primaryAcceptanceLabel"
                :acceptedLabel="acceptedLabel"
                middleLabel="Quotes sent"
                :sent-data="primaryAcceptanceData"
                :middle-data="middleAcceptanceData"
                :accepted-data="dashboardStore.acceptanceRate.accepted"
                :increase="dashboardStore.acceptanceRate.increase"
              />

              <BarsChart
                :chartTitle="barsChartTitle"
                :chartDescription="barsChartDescription"
                :currency="dashboardStore.turnover.currency"
                :flightsLabel="flightsLabel"
                :numberOfFlights="dashboardStore.turnover.numberOfFlights"
                :current-year-data="dashboardStore.turnover.currentYear"
                :previous-year-data="dashboardStore.turnover.previousYear"
                :fee="dashboardStore.turnover.fee"
              />
            </div>
          </Transition>

        </div>

      </main>
    </div>
  </div>
</template>

<script setup>
import {computed, onMounted, watch, watchEffect} from 'vue'
import {useAuthStore, useDashboardStore} from '@/stores'

import AirlineSideBar from '@/views/airline/AirlineSideBar.vue'
import AirlineNavBar from '@/views/airline/AirlineNavBar.vue'
import ChartererSideBar from '@/views/charterer/ChartererSideBar.vue'
import ChartererNavBar from '@/views/charterer/ChartererNavBar.vue'

import LoadingOverlay from '@/components/atoms/LoadingOverlay.vue'
import DashboardHeader from '@/components/atoms/DashboardHeader.vue'
import StatCard from '@/components/atoms/StatCard.vue'
import LinesChart from '@/components/atoms/LinesChart.vue'
import BarsChart from '@/components/atoms/BarsChart.vue'

const authStore = useAuthStore()
const dashboardStore = useDashboardStore()

const isAirline = computed(() => authStore.user?.user_type === 'airline')

const sidebarComponent = computed(() => isAirline.value ? AirlineSideBar : ChartererSideBar)
const navbarComponent = computed(() => isAirline.value ? AirlineNavBar : ChartererNavBar)

// Acceptance rate chart — airline has 3 curves (received/quoted/accepted),
// charterer/admin have 2 (sent/accepted).
const primaryAcceptanceData  = computed(() =>
  isAirline.value ? dashboardStore.acceptanceRate.received : dashboardStore.acceptanceRate.sent
)
const primaryAcceptanceLabel = computed(() =>
  isAirline.value ? 'Requests received' : 'Requests sent'
)
const middleAcceptanceData   = computed(() =>
  isAirline.value ? dashboardStore.acceptanceRate.quoted : []
)
const acceptedLabel = computed(() => isAirline.value ? 'Quotes accepted' : 'Quoted')

const linesChartDescription = computed(() =>
  isAirline.value
    ? 'Requests received, quotes sent, and quotes accepted over the current year'
    : 'Requests sent versus quotes received over the current year'
)
const barsChartTitle = computed(() => isAirline.value ? 'Turnover' : 'Cost of Charters')
const barsChartDescription = computed(() =>
  isAirline.value ? 'Amount of money made in the period' : 'Total spend with airlines'
)
const flightsLabel = computed(() =>
  isAirline.value ? 'No of flights completed' : 'No of flights operated'
)

async function toggleMock() {
  dashboardStore.useMock = !dashboardStore.useMock
  await dashboardStore.fetchDashboardData()
}

watchEffect(() => {
  console.group('[Dashboard]')
  console.log('user',           authStore.user)
  console.log('isAirline',      isAirline.value)
  console.log('useMock',        dashboardStore.useMock)
  console.log('loading',        dashboardStore.loading)
  console.log('stats',          [...dashboardStore.stats])
  console.log('acceptanceRate', {...dashboardStore.acceptanceRate})
  console.log('turnover',       {...dashboardStore.turnover})
  console.groupEnd()
})

// Re-fetch when auth resolves on first browser launch (user starts as {} before
// verifyWithTimeout completes, so the initial fetch may use wrong/missing user_type)
watch(
  () => authStore.user?.id,
  (id, prevId) => { if (id && !prevId) dashboardStore.fetchDashboardData() }
)
onMounted(async () => {
  await dashboardStore.fetchDashboardData()
})
</script>

<style scoped>
.stat-fade-enter-active,
.stat-fade-leave-active {
  transition: opacity 0.2s ease;
}
.stat-fade-enter-from,
.stat-fade-leave-to {
  opacity: 0;
}
</style>
